/**
 * Uni SMS — React pages.  Companion to ``UniEmailPages.jsx``; same
 * structure, different domain (DLT-compliant SMS instead of email).
 *
 * Pages exported:
 *   * UniSmsDashboard     — landing KPIs + quick links.
 *   * UniSmsContacts      — phone-number subscribers.
 *   * UniSmsLists         — segments.
 *   * UniSmsDLTTemplates  — DLT-approved SMS templates.
 *   * UniSmsCampaigns     — campaign list.
 *
 * Same primitives as the Email module, themed via ``./uni.css`` —
 * the ``theme="sms"`` flag flips the hero / KPI accent to mint green.
 */
import React from 'react';
import {
  fmtCount, fmtPct, UniActionButton, UniBool, UniConfirmModal, UniKpiCard,
  UniPageShell, UniRowActions, UniSection, UniSpecEditModal, UniSpecViewModal,
  UniStatusPill, UniTable, UniTile, UniToolbar, uniSpecKeys, useUniDelete,
  useUniEdit, useUniList,
} from './UniShared.jsx';


/* ---------- Field specs --------------------------------------------- *
 * Same arrangement as ``UniEmailPages.jsx``: these drive the Edit and
 * View modals.  Writable keys are taken from
 * ``uni_sms/api_serializers.py``; delivery counters, rates and cost are
 * ``read_only_fields`` there (they're written by the SNS webhook /
 * sender service) so they're ``editable: false`` here — visible in
 * View, never PATCHed.  Choices mirror ``uni_sms/models.py``.
 *
 * ``normalized_phone`` is deliberately absent from the editable set:
 * the model derives it from ``phone`` on save.                         */

const SMS_CONTACT_STATUS = [
  { value: 'active',       label: 'Active' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
  { value: 'blocked',      label: 'Blocked / DND' },
  { value: 'invalid',      label: 'Invalid Number' },
  { value: 'inactive',     label: 'Inactive' },
];

const DLT_TEMPLATE_TYPE = [
  { value: 'Service Explicit', label: 'Service Explicit' },
  { value: 'Service Implicit', label: 'Service Implicit' },
  { value: 'Promotional',      label: 'Promotional' },
  { value: 'Transactional',    label: 'Transactional' },
];

const DLT_INTERNAL_CATEGORY = [
  { value: 'welcome',      label: 'Welcome / Registration' },
  { value: 'otp',          label: 'OTP / Login' },
  { value: 'order',        label: 'Order Related' },
  { value: 'payment',      label: 'Payment Related' },
  { value: 'kyc',          label: 'KYC / Profile' },
  { value: 'meeting',      label: 'Meeting / Training' },
  { value: 'promotion',    label: 'Promotion / Offer' },
  { value: 'notification', label: 'General Notification' },
  { value: 'business',     label: 'Business Summary' },
];

const SMS_TYPE = [
  { value: 'Transactional', label: 'Transactional' },
  { value: 'Promotional',   label: 'Promotional' },
];

const SMS_CAMPAIGN_STATUS = [
  { value: 'draft',     label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'sending',   label: 'Sending' },
  { value: 'paused',    label: 'Paused' },
  { value: 'sent',      label: 'Sent' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'failed',    label: 'Failed' },
];

const SMS_CONTACT_FIELDS = [
  { key: 'phone',  label: 'Phone', required: true,
    hint: 'Include the country code, e.g. +919876543210' },
  { key: 'name',   label: 'Name' },
  { key: 'email',  label: 'Email' },
  { key: 'status', label: 'Status', type: 'select', options: SMS_CONTACT_STATUS },
  { key: 'tags',   label: 'Tags', hint: 'Comma-separated, e.g. vip, mumbai' },
  { key: 'source', label: 'Source', hint: 'How this contact was added: import, manual, crm…' },
  { key: 'normalized_phone', label: 'Normalized phone', editable: false },
  { key: 'sms_sent',      label: 'SMS sent',      editable: false, render: (r) => fmtCount(r.sms_sent) },
  { key: 'sms_delivered', label: 'SMS delivered', editable: false, render: (r) => fmtCount(r.sms_delivered) },
  { key: 'sms_failed',    label: 'SMS failed',    editable: false, render: (r) => fmtCount(r.sms_failed) },
  { key: 'delivery_rate', label: 'Delivery rate', editable: false, render: (r) => fmtPct(r.delivery_rate) },
];

const SMS_LIST_FIELDS = [
  { key: 'name',        label: 'List name', required: true },
  { key: 'description', label: 'Description', type: 'textarea' },
  { key: 'icon',        label: 'Icon', hint: 'A single emoji shown beside the list name.' },
  { key: 'color',       label: 'Colour', hint: 'Hex value, e.g. #6366f1' },
  { key: 'is_active',   label: 'Active', type: 'check' },
  { key: 'member_count',        label: 'Members', editable: false, render: (r) => fmtCount(r.member_count) },
  { key: 'active_member_count', label: 'Active members', editable: false, render: (r) => fmtCount(r.active_member_count) },
];

const DLT_FIELDS = [
  { key: 'template_name', label: 'Template name', required: true },
  { key: 'template_id',   label: 'DLT template ID', required: true,
    hint: 'The ID issued by the DLT portal — must match exactly or sends are rejected.' },
  { key: 'template_type', label: 'DLT type', type: 'select', options: DLT_TEMPLATE_TYPE },
  { key: 'category',      label: 'DLT category' },
  { key: 'header',        label: 'Header / Sender ID' },
  { key: 'consent_id',    label: 'Consent ID' },
  { key: 'entity_id',     label: 'Entity ID' },
  { key: 'content_template', label: 'Content', type: 'textarea', rows: 5,
    hint: 'Use {#var#} placeholders exactly as approved on the DLT portal.' },
  { key: 'internal_category', label: 'Internal category', type: 'select',
    options: DLT_INTERNAL_CATEGORY },
  { key: 'approval_date', label: 'Approval date', hint: 'YYYY-MM-DD' },
  { key: 'notes',         label: 'Notes', type: 'textarea' },
  { key: 'is_active',     label: 'Active', type: 'check' },
  { key: 'preview_content', label: 'Preview', editable: false },
  { key: 'variable_count',  label: 'Variables', editable: false, render: (r) => fmtCount(r.variable_count) },
  { key: 'used_count',      label: 'Times used', editable: false, render: (r) => fmtCount(r.used_count) },
];

const SMS_CAMPAIGN_FIELDS = [
  { key: 'name',        label: 'Campaign name', required: true },
  { key: 'description', label: 'Description', type: 'textarea' },
  { key: 'message_content', label: 'Message', type: 'textarea', rows: 5,
    hint: 'Must match the approved DLT template once variables are filled.' },
  { key: 'sender_id',   label: 'Sender ID' },
  { key: 'sms_type',    label: 'SMS type', type: 'select', options: SMS_TYPE },
  { key: 'status',      label: 'Status', type: 'select', options: SMS_CAMPAIGN_STATUS },
  { key: 'segment_tags', label: 'Only send to tags', hint: 'Comma-separated. Blank = the whole audience.' },
  { key: 'exclude_tags', label: 'Exclude tags', hint: 'Comma-separated.' },
  { key: 'total_recipients', label: 'Recipients', editable: false, render: (r) => fmtCount(r.total_recipients) },
  { key: 'sent_count',       label: 'Sent',      editable: false, render: (r) => fmtCount(r.sent_count) },
  { key: 'delivered_count',  label: 'Delivered', editable: false, render: (r) => fmtCount(r.delivered_count) },
  { key: 'failed_count',     label: 'Failed',    editable: false, render: (r) => fmtCount(r.failed_count) },
  { key: 'delivery_rate',    label: 'Delivery rate', editable: false, render: (r) => fmtPct(r.delivery_rate) },
  { key: 'cost_inr',         label: 'Cost (INR)', editable: false },
];



const EP = {
  CONTACTS:      '/api/uni-sms/contacts/',
  LISTS:         '/api/uni-sms/lists/',
  DLT_TEMPLATES: '/api/uni-sms/dlt-templates/',
  CAMPAIGNS:     '/api/uni-sms/campaigns/',
};

const THEME = 'sms';


/* ====================================================================
   Dashboard
   ==================================================================== */

export function UniSmsDashboard() {
  const contacts  = useUniList(EP.CONTACTS      + '?page=1&page_size=1');
  const lists     = useUniList(EP.LISTS         + '?page=1&page_size=1');
  const templates = useUniList(EP.DLT_TEMPLATES + '?page=1&page_size=1');
  const campaigns = useUniList(EP.CAMPAIGNS     + '?page=1&page_size=1');

  const loading = contacts.loading || lists.loading || campaigns.loading || templates.loading;
  const v = (raw) => (loading ? '…' : fmtCount(raw?.count));

  return (
    <UniPageShell
      theme={THEME}
      icon="💬"
      title="SMS / DLT"
      subtitle="DLT-compliant SMS campaigns, contact lists, and delivery analytics."
      actions={
        <>
          <UniActionButton hero primary to="/uni-sms/campaigns">📣 Campaigns</UniActionButton>
          <UniActionButton hero to="/uni-sms/contacts">Contacts</UniActionButton>
          <UniActionButton hero to="/uni-sms/dlt-templates">DLT Templates</UniActionButton>
        </>
      }
    >
      <UniSection title="At a glance" />
      <div className="uni-kpis">
        {/* Each count links straight to the list behind it, so the
            dashboard answers "which ones?" in one click. */}
        <UniKpiCard icon="📱" label="Contacts"      value={v(contacts.raw)}  sublabel="phone numbers"
                    to="/uni-sms/contacts" />
        <UniKpiCard icon="📂" label="Lists"         value={v(lists.raw)}     sublabel="segments"
                    to="/uni-sms/lists" />
        <UniKpiCard icon="📝" label="DLT Templates" value={v(templates.raw)} sublabel="approved"
                    to="/uni-sms/dlt-templates" />

        <UniKpiCard icon="📣" label="Campaigns"     value={v(campaigns.raw)} sublabel="all-time"
                    to="/uni-sms/campaigns" />

      </div>

      <UniSection title="Manage" />
      <div className="uni-tiles">
        <UniTile to="/uni-sms/contacts"      icon="📱" title="Contacts"
                 sub="Phone-number subscribers and engagement" />
        <UniTile to="/uni-sms/lists"         icon="📂" title="Lists & Segments"
                 sub="Group your audience for targeted SMS" />
        <UniTile to="/uni-sms/dlt-templates" icon="📝" title="DLT Templates"
                 sub="DLT-approved templates with sender IDs" />
        <UniTile to="/uni-sms/campaigns"     icon="📣" title="Campaigns"
                 sub="Drafts, scheduled and sent SMS blasts" />
      </div>
    </UniPageShell>
  );
}


/* ====================================================================
   Contacts
   ==================================================================== */

export function UniSmsContacts() {
  const [q, setQ] = React.useState('');
  const url = q ? `${EP.CONTACTS}?search=${encodeURIComponent(q)}` : EP.CONTACTS;
  const { rows, loading, error, raw, reload } = useUniList(url, [q]);
  const [viewing, setViewing] = React.useState(null);
  const edit = useUniEdit(EP.CONTACTS, () => reload());
  const del  = useUniDelete(EP.CONTACTS, () => reload());

  const columns = [

    { key: 'phone', label: 'Phone',
      render: (r) => <span style={{ fontWeight: 600, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{r.phone}</span> },
    { key: 'name',  label: 'Name' },
    { key: 'email', label: 'Email',
      render: (r) => <span className="uni-num--muted">{r.email || '—'}</span> },
    { key: 'status', label: 'Status',
      render: (r) => <UniStatusPill value={r.status} /> },
    { key: 'sms_sent',      label: 'Sent',      align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.sms_sent)}</span> },
    { key: 'sms_delivered', label: 'Delivered', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.sms_delivered)}</span> },
    { key: 'sms_failed',    label: 'Failed',    align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.sms_failed)}</span> },
    { key: 'actions', label: 'Actions', align: 'right',
      render: (r) => (
        <UniRowActions items={[
          { key: 'view', label: 'View', title: 'Full contact record and delivery history',
            onClick: () => setViewing(r) },
          { key: 'edit', label: 'Edit', title: 'Edit this contact',
            onClick: () => edit.start(r, uniSpecKeys(SMS_CONTACT_FIELDS)) },
          { key: 'delete', label: 'Delete', danger: true,
            title: 'Permanently delete this contact',
            onClick: () => del.ask(r) },
        ]} />
      ) },
  ];
  return (
    <UniPageShell
      theme={THEME}
      icon="📱"
      title="SMS Contacts"
      subtitle={raw?.count != null
        ? `${fmtCount(raw.count)} subscriber${raw.count === 1 ? '' : 's'}`
        : 'Phone-number subscribers'}
      actions={<UniActionButton hero to="/uni-sms">← Dashboard</UniActionButton>}
    >
      <UniToolbar onSearch={setQ} placeholder="Search by phone or name…" />
      <UniTable
        rows={rows} loading={loading} error={error} columns={columns} rowKey="id"
        empty="No contacts yet"
        emptyHint="Import a CSV of phone numbers or sync via the API."
      />
      <UniSpecViewModal row={viewing} onClose={() => setViewing(null)}
                        title="Contact details" fields={SMS_CONTACT_FIELDS} />
      <UniSpecEditModal edit={edit} title="Edit contact" fields={SMS_CONTACT_FIELDS} />
      <UniConfirmModal state={del} title="Delete contact" confirmLabel="Delete contact">
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
          Delete <strong>{del.row?.phone}</strong> and their delivery history?
          This can't be undone.
        </p>
        {/* Consent and DND state are compliance artefacts under TRAI/DLT
            rules — deleting the row throws away the proof that the person
            opted out.  Steer towards the status change. */}
        <div className="uni-note uni-note--warn" style={{ marginTop: 12 }}>
          <span aria-hidden="true">💡</span>
          <span>
            To stop texting this number while keeping the opt-out on record,
            set their status to <strong>Unsubscribed</strong> or{' '}
            <strong>Blocked / DND</strong> instead.
          </span>
        </div>
      </UniConfirmModal>
    </UniPageShell>
  );
}



/* ====================================================================
   Lists & Segments
   ==================================================================== */

export function UniSmsLists() {
  const { rows, loading, error, raw, reload } = useUniList(EP.LISTS);
  const [viewing, setViewing] = React.useState(null);
  const edit = useUniEdit(EP.LISTS, () => reload());
  const del  = useUniDelete(EP.LISTS, () => reload());

  const columns = [
    { key: 'name', label: 'List',
      render: (r) => <span style={{ fontWeight: 600 }}>{r.name}</span> },
    { key: 'description', label: 'Description',
      render: (r) => <span className="uni-num--muted">{r.description || '—'}</span> },
    { key: 'member_count', label: 'Members', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.member_count)}</span> },
    { key: 'active_member_count', label: 'Active', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.active_member_count)}</span> },
    { key: 'is_active', label: 'Active?', align: 'center',
      render: (r) => <UniBool value={r.is_active} /> },
    { key: 'actions', label: 'Actions', align: 'right',
      render: (r) => (
        <UniRowActions items={[
          { key: 'view', label: 'View', title: 'List settings and membership counts',
            onClick: () => setViewing(r) },
          { key: 'edit', label: 'Edit', title: 'Edit this list',
            onClick: () => edit.start(r, uniSpecKeys(SMS_LIST_FIELDS)) },
          { key: 'delete', label: 'Delete', danger: true,
            title: 'Delete this list (contacts are kept)',
            onClick: () => del.ask(r) },
        ]} />
      ) },
  ];
  return (
    <UniPageShell
      theme={THEME}
      icon="📂"
      title="Lists & Segments"
      subtitle={raw?.count != null
        ? `${fmtCount(raw.count)} list${raw.count === 1 ? '' : 's'}`
        : 'Group your audience'}
      actions={<UniActionButton hero to="/uni-sms">← Dashboard</UniActionButton>}
    >
      <UniTable
        rows={rows} loading={loading} error={error} columns={columns} rowKey="id"
        empty="No lists yet"
        emptyHint="Create a list to start segmenting subscribers."
      />
      <UniSpecViewModal row={viewing} onClose={() => setViewing(null)}
                        title="List details" fields={SMS_LIST_FIELDS} />
      <UniSpecEditModal edit={edit} title="Edit list" fields={SMS_LIST_FIELDS} />
      <UniConfirmModal state={del} title="Delete list" confirmLabel="Delete list">
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
          Delete the list <strong>{del.row?.name}</strong>?
          {del.row?.member_count > 0 && (
            <>
              {' '}Its <strong>{fmtCount(del.row.member_count)}</strong>{' '}
              member{del.row.member_count === 1 ? '' : 's'} stay in your
              contacts — only the grouping is removed.
            </>
          )}
        </p>
      </UniConfirmModal>
    </UniPageShell>
  );
}



/* ====================================================================
   DLT Templates
   ==================================================================== */

export function UniSmsDLTTemplates() {
  const { rows, loading, error, raw, reload } = useUniList(EP.DLT_TEMPLATES);
  const [viewing, setViewing] = React.useState(null);
  const edit = useUniEdit(EP.DLT_TEMPLATES, () => reload());
  const del  = useUniDelete(EP.DLT_TEMPLATES, () => reload());

  const columns = [

    { key: 'template_name', label: 'Name',
      render: (r) => <span style={{ fontWeight: 600 }}>{r.template_name}</span> },
    { key: 'template_id',   label: 'DLT ID',
      render: (r) => <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}>{r.template_id}</span> },
    { key: 'template_type', label: 'Type',
      render: (r) => r.template_type ? <UniStatusPill value={r.template_type} palette="cyan" /> : '—' },
    { key: 'category',      label: 'Category',
      render: (r) => r.category ? <UniStatusPill value={r.category} palette="violet" /> : '—' },
    { key: 'used_count',    label: 'Used', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.used_count)}</span> },
    { key: 'is_active', label: 'Active?', align: 'center',
      render: (r) => <UniBool value={r.is_active} /> },
    { key: 'actions', label: 'Actions', align: 'right',
      render: (r) => (
        <UniRowActions items={[
          { key: 'view', label: 'View', title: 'Registered DLT details and content',
            onClick: () => setViewing(r) },
          { key: 'edit', label: 'Edit', title: 'Edit this DLT template',
            onClick: () => edit.start(r, uniSpecKeys(DLT_FIELDS)) },
          { key: 'delete', label: 'Delete', danger: true,
            title: 'Permanently delete this DLT template',
            onClick: () => del.ask(r) },
        ]} />
      ) },
  ];
  return (
    <UniPageShell
      theme={THEME}
      icon="📝"
      title="DLT Templates"
      subtitle={raw?.count != null
        ? `${fmtCount(raw.count)} template${raw.count === 1 ? '' : 's'}`
        : 'DLT-approved SMS templates'}
      actions={<UniActionButton hero to="/uni-sms">← Dashboard</UniActionButton>}
    >
      <UniTable
        rows={rows} loading={loading} error={error} columns={columns} rowKey="id"
        empty="No DLT templates yet"
        emptyHint="Register approved DLT templates to use them in campaigns."
      />
      <UniSpecViewModal row={viewing} onClose={() => setViewing(null)}
                        title="DLT template details" fields={DLT_FIELDS} width={640} />
      {/* The content has to stay byte-identical to what the DLT portal
          approved — editing it here doesn't re-approve it, and a mismatch
          means the operator rejects the send.  The per-field hints in
          ``DLT_FIELDS`` carry that warning into the form. */}
      <UniSpecEditModal edit={edit} title="Edit DLT template"
                        fields={DLT_FIELDS} width={640} />

      <UniConfirmModal state={del} title="Delete DLT template" confirmLabel="Delete template">
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
          Delete <strong>{del.row?.template_name}</strong> (DLT ID{' '}
          <strong>{del.row?.template_id}</strong>)?
          {del.row?.used_count > 0 && (
            <>
              {' '}It has been used in <strong>{fmtCount(del.row.used_count)}</strong>{' '}
              campaign{del.row.used_count === 1 ? '' : 's'}.
            </>
          )}
          {' '}This only removes it from Taskydo — the registration on the DLT
          portal is unaffected.
        </p>
        <div className="uni-note uni-note--warn" style={{ marginTop: 12 }}>
          <span aria-hidden="true">💡</span>
          <span>
            To stop it being picked for new campaigns without losing the
            registration details, uncheck <strong>Active</strong> in Edit.
          </span>
        </div>
      </UniConfirmModal>
    </UniPageShell>
  );
}



/* ====================================================================
   Campaigns
   ==================================================================== */

export function UniSmsCampaigns() {
  const { rows, loading, error, raw, reload } = useUniList(EP.CAMPAIGNS);
  const [viewing, setViewing] = React.useState(null);
  const edit = useUniEdit(EP.CAMPAIGNS, () => reload());
  const del  = useUniDelete(EP.CAMPAIGNS, () => reload());

  const columns = [

    { key: 'name', label: 'Name',
      render: (r) => <span style={{ fontWeight: 600 }}>{r.name}</span> },
    { key: 'sender_id', label: 'Sender',
      render: (r) => r.sender_id ? <UniStatusPill value={r.sender_id} palette="cyan" /> : '—' },
    { key: 'sms_type', label: 'Type',
      render: (r) => r.sms_type ? <UniStatusPill value={r.sms_type} palette="violet" /> : '—' },
    { key: 'status', label: 'Status',
      render: (r) => <UniStatusPill value={r.status} /> },
    { key: 'total_recipients', label: 'Recipients', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.total_recipients)}</span> },
    { key: 'sent_count', label: 'Sent', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.sent_count)}</span> },
    { key: 'delivered_count', label: 'Delivered', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.delivered_count)}</span> },
    { key: 'failed_count', label: 'Failed', align: 'right',
      render: (r) => <span className="uni-num">{fmtCount(r.failed_count)}</span> },
    // Mid-send campaigns are owned by the sender service — same guard as
    // the email side.  Editing the message under it would produce a blast
    // where half the audience got different (possibly non-DLT-compliant)
    // text, so Edit/Delete stay hidden until it settles.
    { key: 'actions', label: 'Actions', align: 'right',
      render: (r) => {
        const inFlight = r.status === 'sending';
        return (
          <UniRowActions items={[
            { key: 'view', label: 'View',
              title: 'Full campaign settings and delivery stats',
              onClick: () => setViewing(r) },
            { key: 'edit', label: 'Edit',
              show: !inFlight,
              title: 'Edit this campaign',
              onClick: () => edit.start(r, uniSpecKeys(SMS_CAMPAIGN_FIELDS)) },
            { key: 'delete', label: 'Delete', danger: true,
              show: !inFlight,
              title: 'Permanently delete this campaign and its stats',
              onClick: () => del.ask(r) },
          ]} />
        );
      } },
  ];
  return (
    <UniPageShell
      theme={THEME}
      icon="📣"
      title="SMS Campaigns"
      subtitle={raw?.count != null
        ? `${fmtCount(raw.count)} campaign${raw.count === 1 ? '' : 's'}`
        : 'Drafts, scheduled and sent SMS campaigns'}
      actions={<UniActionButton hero to="/uni-sms">← Dashboard</UniActionButton>}
    >
      <UniTable
        rows={rows} loading={loading} error={error} columns={columns} rowKey="id"
        empty="No campaigns yet"
        emptyHint="Compose your first SMS campaign from an approved DLT template."
      />
      <UniSpecViewModal row={viewing} onClose={() => setViewing(null)}
                        title="Campaign details" fields={SMS_CAMPAIGN_FIELDS} width={640} />
      <UniSpecEditModal edit={edit} title="Edit campaign"
                        fields={SMS_CAMPAIGN_FIELDS} width={640} />
      <UniConfirmModal state={del} title="Delete campaign" confirmLabel="Delete campaign">
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
          Delete <strong>{del.row?.name}</strong>?
          {del.row?.sent_count > 0 && (
            <>
              {' '}This campaign already reached{' '}
              <strong>{fmtCount(del.row.sent_count)}</strong> recipient
              {del.row.sent_count === 1 ? '' : 's'}; deleting it also removes
              its delivery and failure history.
            </>
          )}
          {' '}This can't be undone.
        </p>
        {del.row?.sent_count > 0 && (
          <div className="uni-note uni-note--warn" style={{ marginTop: 12 }}>
            <span aria-hidden="true">💡</span>
            <span>
              Delivery records for sent SMS are worth keeping for DLT audits —
              consider setting the status to <strong>Cancelled</strong> instead
              of deleting.
            </span>
          </div>
        )}
      </UniConfirmModal>
    </UniPageShell>
  );
}

